import React from 'react'

export type FunnelBenchmarkKey = 'registration' | 'meeting' | 'sale'
export type FunnelStageKey = 'visit' | FunnelBenchmarkKey

export const FUNNEL_STAGE_SUBTITLES: Record<FunnelStageKey, string> = {
    visit: 'Top-of-funnel traffic',
    registration: 'Visit to registration conversion',
    meeting: 'Registration to meeting conversion',
    sale: 'Meeting to sale conversion',
}

export const FUNNEL_MARKET_REFERENCE: Array<{
    key: FunnelBenchmarkKey
    title: string
    subtitle: string
    marketRate: string
}> = [
    {
        key: 'registration',
        title: 'Registrations',
        subtitle: 'Visit to registration conversion',
        marketRate: '0.5%',
    },
    {
        key: 'meeting',
        title: 'Meetings',
        subtitle: 'Registration to meeting conversion',
        marketRate: '20%',
    },
    {
        key: 'sale',
        title: 'Sales',
        subtitle: 'Meeting to sale conversion',
        marketRate: '20%',
    },
]

const DEFAULT_STAGE_ORDER: FunnelStageKey[] = ['visit', 'registration', 'meeting', 'sale']
const DEFAULT_STAGE_TITLES: Record<FunnelStageKey, string> = {
    visit: 'Visits',
    registration: 'Registrations',
    meeting: 'Meetings',
    sale: 'Sales',
}

export function getMarketReferenceRate(key: FunnelStageKey): string {
    const match = FUNNEL_MARKET_REFERENCE.find((benchmark) => benchmark.key === key)
    return match?.marketRate || '—'
}

export interface FunnelLegendRow {
    key: FunnelStageKey
    title: string
    subtitle: string
    ourRate?: string
    marketRate?: string
    count?: number
    color?: string
}

interface CACBenchmarkLegendProps {
    compact?: boolean
    dense?: boolean
    conversionRates?: Partial<Record<FunnelStageKey, string>>
    rows?: FunnelLegendRow[]
    showBadges?: boolean
    title?: string
}

export function CACBenchmarkLegend(props: CACBenchmarkLegendProps) {
    const { compact, dense, conversionRates, rows, showBadges, title = 'Market Reference' } = props

    const legendRows: FunnelLegendRow[] = rows || DEFAULT_STAGE_ORDER.map((key) => ({
        key,
        title: DEFAULT_STAGE_TITLES[key],
        subtitle: FUNNEL_STAGE_SUBTITLES[key],
        ourRate: conversionRates?.[key] || '—',
        marketRate: getMarketReferenceRate(key),
    }))

    if (dense) {
        const displayRows = legendRows.filter((r) => r.key !== 'visit')
        return (
            <div className="cac-benchmark-legend dense">
                <div className="dense-title">{title}</div>
                {displayRows.map((benchmark, i) => {
                    const mktRate = benchmark.marketRate || getMarketReferenceRate(benchmark.key)
                    return (
                        <div key={i} className="dense-row">
                            <span className="dense-stage">
                                {showBadges && (
                                    <span className="dense-dot" style={{ backgroundColor: benchmark.color || '#c0c4c8' }} />
                                )}
                                {benchmark.title}
                            </span>
                            <span className="dense-rate">{benchmark.ourRate || '—'}</span>
                            {mktRate && mktRate !== '—' && (
                                <span className="dense-mkt">/ {mktRate}</span>
                            )}
                        </div>
                    )
                })}
            </div>
        )
    }

    if (compact) {
        return (
            <div className="cac-benchmark-legend compact">
                <span className="benchmark-label">Market Reference:</span>
                {legendRows.filter((x) => x.key !== 'visit').map((benchmark, i) => (
                    <span key={i} className="benchmark-item">
                        <span className="benchmark-stage">{benchmark.title}</span>
                        <span className="benchmark-rate">{benchmark.marketRate || '—'}</span>
                        {i < legendRows.length - 2 && <span className="benchmark-divider">|</span>}
                    </span>
                ))}
            </div>
        )
    }

    return (
        <div className="cac-benchmark-legend funnel-template">
            <div className="benchmark-header">
                <div className="benchmark-section-title">{title}</div>
            </div>
            {legendRows.map((benchmark, i) => {
                const mktRate = benchmark.marketRate || getMarketReferenceRate(benchmark.key)
                return (
                    <div key={i} className="benchmark-row">
                        <div className="benchmark-copy">
                            <div className="benchmark-stage-line">
                                {showBadges && <span className="benchmark-stage-dot" style={{ backgroundColor: benchmark.color || '#c0c4c8' }} />}
                                <div className="benchmark-stage">{benchmark.title}</div>
                            </div>
                        </div>
                        <div className="benchmark-values">
                            {benchmark.count != null && (
                                <div className="benchmark-metric-row">
                                    <div className="benchmark-metric-label">Count</div>
                                    <div className="benchmark-count">{benchmark.count.toLocaleString()}</div>
                                </div>
                            )}
                            <div className="benchmark-metric-row">
                                <div className="benchmark-metric-label">Our Conversion</div>
                                <div className="benchmark-rate">{benchmark.ourRate || '—'}</div>
                            </div>
                            {mktRate && mktRate !== '—' && (
                                <div className="benchmark-metric-row">
                                    <div className="benchmark-metric-label">Market Reference</div>
                                    <div className="benchmark-market-rate">{mktRate}</div>
                                </div>
                            )}
                        </div>
                    </div>
                )
            })}
        </div>
    )
}
